import {
    Grid,
    Paper,
    Typography,
    Chip,
    Box,
    Divider
} from "@mui/material";
import DialogFullScreen from "../../../../../../components/DialogFullScreen";
import LoadingApiQuery from "../../../../../../components/LoadingApiQuery";
import { useQuery } from "react-query";
import axiosClient from "../../../../../../lib/axios";
import { useMemo, useState } from "react";
import CardItensSolicitacao from "./CardItensSolicitacao";
import CardCotacoesRealizadas from "./CardCotacoesRealizadas";
import CardGerarOc from "./CardGerarOc";
import DialogGerarOc from "./DialogGerarOc";
import { ModalDetalheProdutoProvider } from "../../../MIProdutos/ModalDetalheProdutoContext";
import AlertDisplay from "../../../../../../components/BasicComponents/DisplayDados/AlertDisplay";

export default function DialogDetalheSolicitacao({ open, value, handleClose }: { open: boolean; value: string; handleClose: () => void }) {
    const [gerarOcOpen, setGerarOcOpen] = useState(false);

    const { data, isLoading } = useQuery<DetalheSolicitacaoData>({
        queryKey: ["detalhe-solicitacao-compra"],
        queryFn: async () => {
            if (!open) return undefined as any;
            const res = await axiosClient.get(`/estoque/compras/detalhar-solicitacao/${value}`);
            return res.data;
        },
        enabled: !!open && !!value,
        cacheTime: 0,
    });

    const todasOrdensEmitidas = useMemo(() => {
        if (!data) return false;
        return data.itens.every(item => item.status_cotacao === "OC Emitida");
    }, [data]);

    const mostrarAlerta = useMemo(() => {
        if (!data) return false;

        const temCotacoesRespondidas = data.cotacoes.length > 0 &&
            data.cotacoes.some(cotacao => cotacao.respondido_em !== null);

        return temCotacoesRespondidas && !todasOrdensEmitidas;
    }, [data, todasOrdensEmitidas]);

    function mapItemSolicitacaoToProduto(
        itens: ItemSolicitacao[],
        cotacoes: CotacaoRealizada[]
    ): ProdutoGerarOc[] {
        return itens.map((item) => {
            let cotacao_codigo = "";
            let cotacao_fornecedor = "";
            let valor_unitario = 0;
            let valor_total = 0;
            let prazo_entrega = "";
            let fornecedor_id = "";

            for (const cotacao of cotacoes) {
                const found = cotacao.itens.find(i => i.produto_id === item.produto_id);
                if (found) {
                    cotacao_codigo = cotacao.id.toString();
                    cotacao_fornecedor = cotacao.fornecedor;
                    fornecedor_id = cotacao.fornecedor_id;
                    valor_unitario = Number(found.valor_unitario) || 0;
                    valor_total = Number(found.valor_total) || 0;
                    prazo_entrega = `${cotacao.prazo_entrega} DIAS`;
                    break;
                }
            }

            return {
                ...item,
                valor_unitario,
                valor_total,
                prazo_entrega,
                cotacao_codigo,
                cotacao_fornecedor,
                fornecedor_id,
                valor_desconto: 'R$ 0,00',
                valor_acrescimo: 'R$ 0,00',
            };
        });
    }

    return (
        <DialogFullScreen open={open} handleClose={handleClose} title="Detalhes da solicitação">
            {isLoading && <LoadingApiQuery />}
            {!isLoading && data && (
                <>
                    {mostrarAlerta && (
                        <Box mb={2}>
                            <AlertDisplay
                                titulo="Ordem de Compra"
                                registro="Existem ordens de compra não informadas"
                                color="warning"
                                acao="Continuar"
                                acaoCallback={() => setGerarOcOpen(true)}
                            />
                        </Box>
                    )}
                    <Grid container spacing={2}>
                        <Grid container size={{ xs: 12, md: 4 }} spacing={2} alignSelf="flex-start">
                            <Grid size={{ xs: 12 }}>
                                <Paper sx={{ p: 2 }}>
                                    <Grid
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="space-between"
                                    >
                                        <Grid>
                                            <Typography
                                                variant="subtitle2"
                                                color="text.secondary"
                                            >
                                                Código
                                            </Typography>
                                            <Typography variant="h6" sx={{ mb: 1 }}>
                                                {data.codigo}
                                            </Typography>
                                        </Grid>
                                        <Chip
                                            label={data.status}
                                            sx={{
                                                background: "#4CAF50",
                                                color: "#fff",
                                                fontWeight: "bold",
                                                mb: 2,
                                            }}
                                        />
                                    </Grid>
                                    <Typography variant="body2" sx={{ mt: 1 }}>
                                        Solicitado em<br />
                                        <b>{data.data}</b>
                                    </Typography>
                                    <Typography variant="body2" sx={{ mt: 1 }}>
                                        Solicitante<br />
                                        <b>{data.solicitante}</b>
                                    </Typography>

                                    {data.observacoes && (
                                        <Box sx={{ mt: 2 }}>
                                            <Divider sx={{ mb: 2 }} />

                                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                                Observações
                                            </Typography>
                                            <Box
                                                sx={{
                                                    backgroundColor: 'grey.50',
                                                    border: '1px solid',
                                                    borderColor: 'grey.300',
                                                    borderRadius: '8px',
                                                    p: 2,
                                                    maxHeight: '150px',
                                                    overflow: 'auto',
                                                    '&::-webkit-scrollbar': {
                                                        width: '6px',
                                                    },
                                                    '&::-webkit-scrollbar-track': {
                                                        backgroundColor: 'grey.100',
                                                        borderRadius: '3px',
                                                    },
                                                    '&::-webkit-scrollbar-thumb': {
                                                        backgroundColor: 'grey.400',
                                                        borderRadius: '3px',
                                                        '&:hover': {
                                                            backgroundColor: 'grey.500',
                                                        },
                                                    },
                                                }}
                                            >
                                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                                    {data.observacoes}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    )}
                                </Paper>
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <CardGerarOc data={data} onGerarOc={() => setGerarOcOpen(true)} />
                            </Grid>
                        </Grid>
                        <Grid container size={{ xs: 12, md: 8 }} spacing={2} alignSelf="flex-start">
                            <Grid size={{ xs: 12 }}>
                                <ModalDetalheProdutoProvider>
                                    <CardItensSolicitacao data={data} />
                                </ModalDetalheProdutoProvider>
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <CardCotacoesRealizadas data={data} />
                            </Grid>
                        </Grid>
                    </Grid>

                    {
                        gerarOcOpen && (
                            <DialogGerarOc
                                open={gerarOcOpen}
                                handleClose={() => setGerarOcOpen(false)}
                                produtos={mapItemSolicitacaoToProduto(data.itens, data.cotacoes)}
                                cotacoes={data.cotacoes}
                                solicitacao_id={data.id}
                                solicitacaoDetalhe={data}
                            />)
                    }
                </>
            )}
        </DialogFullScreen>
    );
}

interface ProdutoGerarOc {
    id: number;
    produto_id: number;
    unidade_medida: string;
    observacoes?: string | null;
    nome: string;
    quantidade: number;
    status_cotacao: string;
    valor_unitario: number;
    valor_total: number;
    prazo_entrega: string;
    cotacao_codigo: string;
    cotacao_fornecedor: string;
    valor_desconto: string;
    valor_acrescimo: string;
    fornecedor_id: string;
}

export interface ItemSolicitacao {
    id: number;
    nome: string;
    quantidade: number;
    status_cotacao: string;
    observacoes?: string;
    produto_id: number;
    unidade_medida: string;
}

export interface OrdemCompra {
    id: number | null;
    codigo: string;
    oc_cancelada: boolean;
    fornecedor: string;
    status: string;
    tipo_cliente: string;
    codigo_cliente: string;
    data_emissao: string;
    tipo_cliente_frete: string;
    codigo_cliente_frete: string;
    codigo_comprador: string;
    nome_vendedor: string;
    data_fechamento: string;
    via_fechamento: string;
    tolerancia: string;
    emitida: string;
    destino: string;
    observacao: string;
    loja_solicitante_id: string;
    numero_solicitacao: string;
    tipo_cliente_solicitante: string;
    codigo_cliente_solicitante: string;
    entrega_garantida: string;
    usuario_garantia: string;
    data_garantia: string;
    nome_garantia: string;
    cif_fob: string;
    data_prevista_entrega: string;
    valor_total?: string | number;
}

export interface DetalheSolicitacaoData {
    id: number;
    codigo: string;
    data: string;
    solicitante: string;
    status: string;
    observacoes?: string;
    itens: ItemSolicitacao[];
    cotacoes: CotacaoRealizada[];
    ordens_compra?: OrdemCompra[];
}
export interface CotacaoImposto {
    aplica: boolean;
    valor: number;
    nome: string;
    tipo: string;
    incluso_preco: boolean;
    direito_credito: boolean;
}

export interface CotacaoItem {
    id: number;
    produto_id: number;
    produto_nome: string;
    unidade_medida: string;
    quantidade: number;
    valor_unitario: number;
    valor_total: number;
    impostos: CotacaoImposto[];
    usuario_alteracao: string | null;
    criado_em: string;
}

export interface CotacaoRealizada {
    id: number;
    fornecedor: string;
    fornecedor_id: string;
    prazo_entrega?: number;
    valor_frete?: string;
    tipo_frete?: string;
    vencimento: string;
    itens: CotacaoItem[];
    respondido_em: string | null;
    criada_em: string;
    criada_por: string;
    url_formulario: string | null;
    observacoes_fornecedor: string | null;
    nome_vendedor: string | null;
    telefone_vendedor: string | null;
    email_vendedor: string | null;
}

export interface CardProps {
    data: DetalheSolicitacaoData;
}